<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { FileText, ChevronDown, Plus, Check, Menu, Zap, Wrench, Braces, BookOpen, Building2, Clock } from 'lucide-vue-next'
import type { Template } from '@/types'

interface Props {
  activeTemplate: Template | null
  allTemplates: Template[]
  getTemplateStatus?: (template: Template) => string
}

const props = withDefaults(defineProps<Props>(), {
  activeTemplate: null,
  getTemplateStatus: (template: Template) => template.r2_key ? 'ready' : 'setup-required'
})

const templateDisplayName = computed(() => {
  if (props.activeTemplate) {
    return props.activeTemplate.name
  }
  // Show appropriate message when no template is selected
  return props.allTemplates.length === 0 ? t('noTemplates') : t('selectTemplate')
})

const emit = defineEmits<{
  'template-selected': [templateId: string]
  'create-template': []
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const showTemplateDropdown = ref(false)
const showNavigationMenu = ref(false)
const dropdownRef = ref<HTMLDivElement | null>(null)
const navigationRef = ref<HTMLDivElement | null>(null)

interface NavigationItem {
  name: string
  label: string
  icon: Component
  route: string
}

// Navigation menu items
const navigationItems = computed((): NavigationItem[] => [
  {
    name: 'Generate',
    label: t('generate'),
    icon: Zap,
    route: '/'
  },
  {
    name: 'TemplateSetup',
    label: t('templateSetup'),
    icon: Wrench,
    route: '/template-setup'
  },
  {
    name: 'Variables',
    label: t('variables'),
    icon: Braces,
    route: '/variables'
  },
  {
    name: 'Generations',
    label: t('generations'),
    icon: Clock,
    route: '/generations'
  },
  {
    name: 'Guide',
    label: t('guide'),
    icon: BookOpen,
    route: '/guide'
  },
  {
    name: 'MyCompany',
    label: t('myCompany'),
    icon: Building2,
    route: '/my-company'
  }
])

const currentRoute = computed(() => {
  return route.name
})

const navigateTo = (routePath: string) => {
  router.push(routePath)
  showNavigationMenu.value = false
}

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    showTemplateDropdown.value = false
  }
  if (navigationRef.value && !navigationRef.value.contains(event.target as Node)) {
    showNavigationMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="border-b border-gray-200 bg-white">
    <div class="max-w-6xl mx-auto w-full px-6 py-4">
      <div class="flex items-center justify-between gap-4">
        <!-- Left Section: Hamburger Menu + Template Dropdown -->
        <div class="flex items-center gap-4 flex-1">
          <!-- Hamburger Menu -->
          <div ref="navigationRef" class="relative">
            <button
              @click="showNavigationMenu = !showNavigationMenu"
              class="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              :title="t('menu')"
            >
              <Menu :size="20" />
            </button>

            <!-- Navigation Dropdown -->
            <transition name="dropdown">
              <div
                v-if="showNavigationMenu"
                class="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl w-56 z-50"
              >
                <div class="p-2">
                  <div
                    v-for="item in navigationItems"
                    :key="item.name"
                    @click="navigateTo(item.route)"
                    :class="[
                      'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors',
                      currentRoute === item.name ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 text-gray-700'
                    ]"
                  >
                    <component :is="item.icon" :size="18" />
                    <span class="text-sm font-medium">{{ item.label }}</span>
                    <Check v-if="currentRoute === item.name" :size="16" class="ml-auto text-blue-600" />
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <div class="h-6 w-px bg-gray-300"></div>

          <!-- Template Dropdown -->
          <div ref="dropdownRef" class="relative flex-1 max-w-xl">
            <button
              @click="showTemplateDropdown = !showTemplateDropdown"
              class="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:border-blue-500 transition-colors"
            >
              <div class="flex items-center gap-3">
                <FileText :size="18" class="text-blue-600" />
                <span class="font-medium text-gray-800">{{ templateDisplayName }}</span>
              </div>
              <ChevronDown :size="18" :class="['text-gray-600 transition-transform', showTemplateDropdown && 'rotate-180']" />
            </button>

            <transition name="dropdown">
              <div
                v-if="showTemplateDropdown"
                class="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50"
              >
                <div class="p-2">
                  <div
                    v-for="template in allTemplates"
                    :key="template.id"
                    @click="emit('template-selected', template.id); showTemplateDropdown = false"
                    :class="[
                      'flex items-center justify-between px-3 py-2.5 rounded-md cursor-pointer transition-colors',
                      activeTemplate?.id === template.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'
                    ]"
                  >
                    <div class="flex items-center gap-3 flex-1">
                      <Check v-if="activeTemplate?.id === template.id" :size="16" class="text-blue-600" />
                      <div v-else class="w-4"></div>
                      <span class="text-sm font-medium">{{ template.name }}</span>
                    </div>
                    <span
                      :class="[
                        'text-xs px-2 py-0.5 rounded-full',
                        getTemplateStatus(template) === 'ready' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      ]"
                    >
                      {{ getTemplateStatus(template) === 'ready' ? '✅' : '⚠️' }}
                    </span>
                  </div>

                  <div class="border-t border-gray-200 mt-2 pt-2">
                    <button
                      @click="emit('create-template'); showTemplateDropdown = false"
                      class="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-blue-50 text-blue-600 transition-colors"
                    >
                      <Plus :size="16" />
                      <span class="text-sm font-medium">{{ t('newTemplate') }}</span>
                    </button>
                  </div>

                  <!-- Slot for additional dropdown items (e.g., Edit Lock) -->
                  <slot name="dropdown-extra"></slot>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
